/**
 * QuestionGroupEditorPage — group builder with branching + tree/preview tabs.
 * Faithful port of QuestionGroupEditorPage from screens-question-editors.jsx.
 */
import { useState, useEffect , type CSSProperties, type ReactNode} from "react";
import { MediaRow, Btn, Card, Dropdown, Field, Icon, IconBtn, Input, T, Toggle } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { EmptyState } from "../../shell/composites";
import { useIsMobile } from "../../shell/useResponsive";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { useQuestions, QTYPE, type Question, type QuestionGroup, type GroupItem } from "./useQuestions";
import { QGlyph } from "./QuestionsLibrary";
import { useT } from "../../lib/i18n";

const qbInput = (extra?: CSSProperties): CSSProperties => ({
  width: "100%", height: 40, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8,
  background: T.white, color: T.n1, fontSize: 13.5, fontFamily: T.font, boxSizing: "border-box", ...extra,
});

/* ── Approved question picker modal ───────────────────────────────────── */
function ApprovedQuestionPicker({ exclude, onPick, onClose }: { exclude: string[]; onPick: (q: Question) => void; onClose: () => void }) {
  const t = useT();
  const store = useQuestions();
  const [q, setQ] = useState("");
  const all = store.questions().filter((x) => !exclude.includes(x.id) && (x.status === "approved" || !q.trim() || x.label.toLowerCase().includes(q.toLowerCase())));
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 250, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: "94vw", maxHeight: "84vh", background: T.white, borderRadius: 14, boxShadow: `0 20px 50px -12px rgba(16,24,40,.4)`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: T.n1, margin: 0 }}>{t("questions.groupEditorPage.addAQuestion", "Add a question")}</h2>
          <IconBtn label={t("common.actions.close", "Close")} onClick={onClose}><Icon.x size={15}/></IconBtn>
        </div>
        <div style={{ padding: "10px 16px", borderBottom: `1px solid ${T.border}` }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("questions.groupEditorPage.searchApprovedQuestions", "Search approved questions…")}
            aria-label={t("questions.groupEditorPage.searchApprovedQuestions", "Search approved questions…")} iconLeft={<Icon.search size={14}/>} size="sm"/>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "8px 12px 12px" }}>
          {all.length === 0 ? <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("questions.groupEditorPage.noMatchingApprovedQuestions", "No matching approved questions.")}</div>
          : all.map((question) => (
            <MediaRow key={question.id} onClick={() => { onPick(question); onClose(); }} leading={<span style={{ width: 30, height: 30, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><QGlyph type={question.type} size={15}/></span>}
              title={<>{question.label}</>} subtitle={<>{QTYPE(question.type).label}</>}
              trailing={<><Icon.plus size={16} style={{ marginLeft: "auto", flexShrink: 0, color: T.soft }}/></>}
              style={{ borderRadius: 9, padding: "10px 12px" }} />

          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Drag handle (2026-08-12, operator): rows reorder by dragging the grip —
 * the up/down movers are gone. ── */
const GripGlyph = () => (
  <svg width="12" height="14" viewBox="0 0 12 14" fill="currentColor" aria-hidden="true"><circle cx="4" cy="3" r="1.2"/><circle cx="8" cy="3" r="1.2"/><circle cx="4" cy="7" r="1.2"/><circle cx="8" cy="7" r="1.2"/><circle cx="4" cy="11" r="1.2"/><circle cx="8" cy="11" r="1.2"/></svg>
);

/* ── Respondent preview — the group rendered as the customer will see it ── */
function QuestionPreviewView({ group }: { group: QuestionGroup }) {
  const t = useT();
  const store = useQuestions();
  const items = group.items || [];
  const control = (q: Question): ReactNode => {
    switch (q.type) {
      case "short": return <input placeholder={q.placeholder || t("questions.groupEditorPage.yourAnswer", "Your answer")} style={qbInput()}/>;
      case "long": return <textarea rows={3} placeholder={q.placeholder || t("questions.groupEditorPage.yourAnswer", "Your answer")} style={{ ...qbInput(), height: "auto", padding: 10, resize: "vertical" }}/>;
      case "number": return <input type="number" placeholder="0" style={qbInput({ width: 140 })}/>;
      case "datetime": return <input type="datetime-local" style={qbInput({ width: 230 })}/>;
      case "radio": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {(q.options || []).map((o) => <label key={o.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.n1, cursor: "pointer" }}><input type="radio" name={`pv_${q.id}`}/> {o.label}</label>)}
        </div>
      );
      case "checks": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {(q.options || []).map((o) => <label key={o.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.n1, cursor: "pointer" }}><input type="checkbox"/> {o.label}</label>)}
        </div>
      );
      case "select": case "multi": {
        const multi = q.type === "multi" || !!q.select?.allowMultiple;
        return (
          <select multiple={multi} style={qbInput({ height: multi ? "auto" : 40, cursor: "pointer" })}>
            {!multi ? <option value="">{t("questions.groupEditorPage.choose", "Choose…")}</option> : null}
            {(q.options || []).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        );
      }
      case "yesno": return (
        <div style={{ display: "flex", gap: 16 }}>
          {[t("common.yes", "Yes"), t("common.no", "No")].map((lb) => <label key={lb} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: T.n1, cursor: "pointer" }}><input type="radio" name={`pv_${q.id}`}/> {lb}</label>)}
        </div>
      );
      case "file": return (
        <div style={{ padding: "18px 14px", border: `1.5px dashed ${T.n4}`, borderRadius: 9, textAlign: "center", fontSize: 12.5, color: T.soft }}>{t("questions.groupEditorPage.dropAFileOrClickToUpload", "Drop a file here, or click to upload")}</div>
      );
      case "stepper": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(q.stepper?.rows?.length ? q.stepper.rows : [{ id: "r1", label: "" }]).map((r) => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {r.label ? <span style={{ fontSize: 13, color: T.n1, minWidth: 90 }}>{r.label}</span> : null}
              <span style={{ display: "inline-flex", alignItems: "center", border: `1px solid ${T.n4}`, borderRadius: 8, overflow: "hidden" }}>
                {/* ui-raw: quantity stepper − / + control — QuantityStepper owns this (group 10) */}
                <button style={{ width: 32, height: 32, border: "none", background: T.canvas, cursor: "pointer", fontSize: 15, color: T.n1 }}>−</button>
                <span style={{ width: 40, textAlign: "center", fontSize: 13.5, color: T.n1 }}>0</span>
                {/* ui-raw: quantity stepper − / + control — QuantityStepper owns this (group 10) */}
                <button style={{ width: 32, height: 32, border: "none", background: T.canvas, cursor: "pointer", fontSize: 15, color: T.n1 }}>+</button>
              </span>
            </div>
          ))}
        </div>
      );
      default: return <input style={qbInput()}/>;
    }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {items.map((it) => {
        const q = store.getQ(it.qid); if (!q) return null;
        const req = it.required != null ? it.required : q.required;
        if (q.type === "heading") return <div key={it.qid} style={{ fontSize: 15.5, fontWeight: 700, color: T.n1, borderBottom: `1px solid ${T.border}`, paddingBottom: 8 }}>{q.label}</div>;
        if (q.type === "label") return <div key={it.qid} style={{ fontSize: 13, color: T.n2, lineHeight: 1.55 }}>{q.label}{q.help ? <div style={{ fontSize: 12, color: T.soft, marginTop: 3 }}>{q.help}</div> : null}</div>;
        const hasRules = it.rules && Object.keys(it.rules).length > 0;
        return (
          <div key={it.qid}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n1, marginBottom: 7 }}>
              {q.label}{req ? <span style={{ color: T.red1, marginLeft: 3 }}>*</span> : null}
              {hasRules ? <span title={t("questions.groupEditorPage.branchesByAnswer", "Branches by answer — see the Flow tree")} style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: T.p1, background: T.p4, borderRadius: 999, padding: "2px 8px", verticalAlign: "middle" }}>{t("questions.groupEditorPage.branchesPill", "branches")}</span> : null}
            </div>
            {q.help ? <div style={{ fontSize: 12, color: T.soft, margin: "-3px 0 7px" }}>{q.help}</div> : null}
            {control(q)}
          </div>
        );
      })}
    </div>
  );
}

/* ── Tree view ──────────────────────────────────────────────────────── */
function QuestionTreeView({ group }: { group: QuestionGroup }) {
  const t = useT();
  const store = useQuestions();
  const items = group.items || [];
  if (items.length === 0) return <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("questions.groupEditorPage.noQuestionsYet9fc9f820", "No questions yet.")}</div>;
  return (
    <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 2 }}>
      {items.map((it, i) => {
        const q = store.getQ(it.qid); if (!q) return null;
        const rules = it.rules ? Object.entries(it.rules) : [];
        return (
          <div key={it.qid}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 6 }}>
              <span style={{ fontSize: 11, color: T.soft, width: 16, textAlign: "center" }}>{i + 1}</span>
              <QGlyph type={q.type} size={13} color={T.soft}/>
              <span style={{ fontSize: 12.5, color: T.n1, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{q.label}</span>
              {rules.length ? <span style={{ fontSize: 11, fontWeight: 600, color: T.p1 }}>{rules.length}{t("questions.groupEditorPage.branch", "×branch")}</span> : null}
            </div>
            {rules.map(([key, target]) => (
              <div key={key} style={{ marginLeft: 32, padding: "2px 8px", fontSize: 11, color: T.soft, display: "flex", alignItems: "center", gap: 6 }}>
                <Icon.arrow dir="right" size={10}/> <b>"{key}"</b> → {target === "__end__" ? t("questions.groupEditorPage.text", "End form") : (() => { const tq = store.getQ(target); return tq ? `Q${items.findIndex((x) => x.qid === target) + 1} · ${tq.label}` : target; })()}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/* ═══ Main editor ═══════════════════════════════════════════════════════ */
export default function QuestionGroupEditorPage() {
  /* One column on a phone (2026-09-24): the two panes had 800px of minimum
     width between them, so a 375px screen showed the settings pane clipped
     and the preview off the edge. Stacked, settings first, preview below. */
  const mobile = useIsMobile();
  const t = useT();
  const nav = useNav();
  const store = useQuestions();
  const isNew = !!(nav.params?.new);
  const viewOnly = !!(nav.params?.view);
  const id = nav.params?.id as string | undefined;
  const canWrite = !viewOnly && can("questions.write");

  const seed = (): QuestionGroup => isNew ? store.newGroup() : (id && store.getGroup(id) ? JSON.parse(JSON.stringify(store.getGroup(id))) : store.newGroup());
  const [form, setForm] = useState<QuestionGroup>(seed);
  const [picking, setPicking] = useState(false);
  const [openRules, setOpenRules] = useState<string | null>(null);
  const [rightTab, setRightTab] = useState<"tree" | "preview">("tree");
  const routeKey = `${isNew ? "new" : id ?? ""}:${viewOnly ? "v" : "e"}`;
  useEffect(() => { setForm(seed()); }, [routeKey]);
  /* COLD LOAD (CC live test, 2026-09-25). Opened by direct link or refresh, the
     seed ran before the library had loaded, found nothing, and fell back to a
     NEW question flow — so the page showed blank fields and Save created a second
     question flow instead of editing this one. Re-seed once the item arrives, and only
     if the form is still the placeholder, so an edit in progress is never
     overwritten by a late load. */
  const hydratedItem = !isNew && id ? store.getGroup(id) : null;
  useEffect(() => {
    if (hydratedItem && form.id !== id) setForm(seed());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydratedItem ? hydratedItem.id : null]);
  /* An id that the loaded library does not contain is an ERROR, never a new
     question flow: showing a blank form there is exactly how the duplicate got made. */
  const notFound = !isNew && !!id && store.loaded() && !hydratedItem;
  const pendingLoad = !isNew && !!id && !store.loaded();

  const items = form.items || [];
  const setItems = (next: GroupItem[] | ((a: GroupItem[]) => GroupItem[])) =>
    setForm((f) => ({ ...f, items: typeof next === "function" ? next(f.items || []) : next }));
  const addQuestion = (q: Question) => { if (items.some((it) => it.qid === q.id)) return; setItems((arr) => [...arr, { qid: q.id }]); };
  const removeItem = (qid: string) => setItems((arr) => arr.filter((it) => it.qid !== qid));
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragArm, setDragArm] = useState<number | null>(null);
  const setRequired = (qid: string, val: boolean) => setItems((arr) => arr.map((it) => it.qid === qid ? { ...it, required: val } : it));
  const setRule = (qid: string, key: string, target: string) => setItems((arr) => arr.map((it) => {
    if (it.qid !== qid) return it;
    const rules = { ...(it.rules || {}) };
    if (!target || target === "__next__") delete rules[key]; else rules[key] = target;
    return { ...it, rules };
  }));

  const branchKeys = (q: Question) => q.type === "yesno" ? [["yes","Yes"],["no","No"]] : (q.options || []).map((o) => [o.id, o.label]);
  const saveDisabled = !form.name.trim() || items.length === 0;
  const totalBranches = items.reduce((n, it) => n + (it.rules ? Object.keys(it.rules).length : 0), 0);

  const save = () => {
    store.upsertGroup({ ...form });
    toast({ tone: "success", title: isNew ? "Group created" : "Group saved", subtitle: t("questions.groupEditorPage.availableForUseInEvents", "Available for use in events.") });
    nav.go("questions.groups");
  };

  /* Cold load and unknown id render their OWN states, never the form (CC, 2026-09-25). */
  if (pendingLoad || notFound) {
    return (
      <Screen rail="cs-questions">
        <div style={{ padding: "48px 24px", display: "flex", justifyContent: "center" }}>
          {pendingLoad
            ? <div role="status" aria-live="polite" style={{ fontSize: 13, color: T.soft }}>{t("questions.groupEditorPage.text5", "Loading the question flow…")}</div>
            : <EmptyState variant="card" icon={<Icon.info size={22}/>} title={t("questions.groupEditorPage.notFoundTitle", "This question flow doesn't exist")}
                body={t("common.notFound.body", "It may have been deleted, or the link is out of date. Nothing was created.")}
                action={<Btn onClick={() => nav.go("questions.groups")}>{t("questions.groupEditorPage.backToFlows", "Back to question flows")}</Btn>}/>}
        </div>
      </Screen>
    );
  }

  return (
    <Screen rail="cs-questions">
      <PageHeader
        title={isNew ? t("questions.questionGroupEditorPage.newQuestionGroup", "New question group") : (viewOnly ? form.name || t("questions.library.group", "Group") : t("questions.questionGroupEditorPage.editFormNameGroup", "Edit · {value0}", { value0: form.name || t("questions.library.group", "Group") }))}
        subtitle={[t("questions.groupEditorPage.questionCount", "{count, plural, one {# question} other {# questions}}", { count: items.length }), totalBranches ? t("questions.groupEditorPage.branchCount", "{count, plural, one {# branch} other {# branches}}", { count: totalBranches }) : ""].filter(Boolean).join(" · ")}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("questions.groups")}><Icon.arrow dir="left" size={11}/> {t("questions.groupEditorPage.groups", "Groups")}</span>}
        actions={viewOnly
          ? <Btn icon={<Icon.edit size={14}/>} onClick={() => nav.go("questions.group.edit", { id: form.id })}>{t("common.actions.edit", "Edit")}</Btn>
          : <>
              <Btn variant="secondary" onClick={() => nav.go("questions.groups")}>{t("common.actions.cancel", "Cancel")}</Btn>
              <Btn icon={<Icon.check size={14}/>} disabled={saveDisabled} onClick={save}>{isNew ? t("questions.questionGroupEditorPage.createGroup", "Create group") : t("common.actions.save", "Save")}</Btn>
            </>}
      />
      <div style={{ flex: mobile ? "0 0 auto" : 1, display: "grid", gridTemplateColumns: mobile ? "minmax(0,1fr)" : "minmax(460px,1fr) minmax(380px,520px)", gridAutoRows: mobile ? "auto" : undefined, minHeight: 0, borderTop: `1px solid ${T.border}`, marginTop: 16 }}>
        {/* Builder panel */}
        <div style={{ borderRight: mobile ? "none" : `1px solid ${T.border}`, borderBottom: mobile ? `1px solid ${T.border}` : "none", overflow: mobile ? "visible" : "auto", padding: "20px 24px 40px", background: T.canvas, opacity: canWrite ? 1 : 0.75, pointerEvents: canWrite ? "auto" : "none" }}>
          <Card style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
            <Field label={t("questions.groupEditorPage.groupName", "Group name")} required htmlFor="qg-name"><Input id="qg-name" autoFocus={isNew} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t("questions.groupEditorPage.eGStandardRegistration", "e.g. Standard registration")} aria-required="true"/></Field>
            <Field label={t("questions.groupEditorPage.description", "Description")} htmlFor="qg-desc"><Input id="qg-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={t("questions.groupEditorPage.whenToUseThisGroup", "When to use this group")}/></Field>
          </Card>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 2px 12px" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("questions.groupEditorPage.questionsInThisGroup", "Questions in this group")}</div>
              <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{t("questions.groupEditorPage.orderedTopBottomAddBranchingOnChoiceQuestions", "Ordered top→bottom. Add branching on choice questions.")}</div>
            </div>
            <Btn variant="secondary" size="sm" icon={<Icon.plus size={13}/>} onClick={() => setPicking(true)}>{t("questions.groupEditorPage.addQuestion", "Add question")}</Btn>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((it, i) => {
              const q = store.getQ(it.qid); if (!q) return null;
              const reqEffective = it.required != null ? it.required : q.required;
              const canBranch = QTYPE(q.type).branch;
              const open = openRules === it.qid;
              const ruleCount = it.rules ? Object.keys(it.rules).length : 0;
              return (
                <div key={it.qid}
                  draggable={dragArm === i}
                  onDragStart={(e) => { setDragIdx(i); try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", it.qid); } catch { /* older browsers */ } }}
                  onDragEnd={() => { setDragIdx(null); setDragArm(null); }}
                  onDragOver={(e) => { e.preventDefault(); if (dragIdx === null || dragIdx === i) return; setItems((arr) => { const n = arr.slice(); const m = n.splice(dragIdx, 1)[0]; n.splice(i, 0, m); return n; }); setDragIdx(i); }}
                  style={{ borderRadius: 12, opacity: dragIdx === i ? 0.55 : 1, boxShadow: dragIdx === i ? `0 0 0 2px ${T.p1}` : undefined }}>
                <Card style={{ padding: 0, overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px" }}>
                    <span role="img" aria-label={t("questions.groupEditorPage.dragToReorder", "Drag to reorder")} title={t("questions.groupEditorPage.dragToReorder", "Drag to reorder")}
                      onMouseDown={() => setDragArm(i)} onMouseUp={() => setDragArm(null)}
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 28, color: T.n4, cursor: "grab", flexShrink: 0 }}><GripGlyph/></span>
                    <span style={{ width: 24, height: 24, borderRadius: 999, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                    <QGlyph type={q.type} size={15} color={T.p1}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{q.label}</div>
                      <div style={{ fontSize: 11.5, color: T.soft }}>{QTYPE(q.type).label}{ruleCount ? " · " + t("questions.groupEditorPage.branchCount", "{count, plural, one {# branch} other {# branches}}", { count: ruleCount }) : ""}</div>
                    </div>
                    <label onClick={() => setRequired(it.qid, !reqEffective)} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                      <Toggle on={!!reqEffective}/><span style={{ fontSize: 12, color: T.soft }}>{t("questions.groupEditorPage.req", "Req")}</span>
                    </label>
                    {canBranch ? <IconBtn size={30} label={t("questions.groupEditorPage.branching", "Branching")} onClick={() => setOpenRules(open ? null : it.qid)} style={{ color: ruleCount ? T.p1 : T.n2 }}><Icon.network size={15}/></IconBtn> : null}
                    <IconBtn size={30} label={t("common.actions.remove", "Remove")} onClick={() => removeItem(it.qid)}><Icon.trash size={14}/></IconBtn>
                  </div>
                  {canBranch && open ? (
                    <div style={{ borderTop: `1px solid ${T.border}`, background: T.p4, padding: "12px 14px 14px" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.p1, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}><Icon.network size={13}/> {t("questions.groupEditorPage.branchingWhenAnswered", "Branching — when answered…")}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                        {branchKeys(q).map(([key, label]) => {
                          const cur = (it.rules || {})[key] || "__next__";
                          const laterQs = items.slice(i + 1);
                          return (
                            <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 12.5, color: T.n1, minWidth: 110, fontWeight: 600 }}>"{label}"</span>
                              <Icon.arrow dir="right" size={12}/>
                              <Dropdown
                                options={[
                                  { value: "__next__", label: t("questions.groupEditorPage.continueInOrder", "Continue in order") },
                                  { value: "__end__", label: t("questions.groupEditorPage.endTheForm", "End the form") },
                                  ...laterQs.map((li, k) => { const lq = store.getQ(li.qid); return lq ? { value: li.qid, label: t("questions.groupEditorPage.skipToQuestion", "Skip to Q{number} · {label}", { number: i + 2 + k, label: lq.label }) } : null; }).filter((o): o is { value: string; label: string } => !!o),
                                ]}
                                value={cur}
                                onChange={(v) => setRule(it.qid, key, v)}
                                aria-label={t("questions.groupEditorPage.branchTarget", "Branch target")}
                                style={{ gap: 0, flex: 1 }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </Card>
                </div>
              );
            })}
            {items.length === 0 ? (
              <div style={{ padding: 30, border: `1.5px dashed ${T.n4}`, borderRadius: 10, textAlign: "center", background: T.white }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{t("questions.groupEditorPage.noQuestionsYet", "No questions yet")}</div>
                <div style={{ fontSize: 12.5, color: T.soft, margin: "4px 0 12px" }}>{t("questions.groupEditorPage.addApprovedQuestionsToBuildTheFlow", "Add approved questions to build the flow.")}</div>
                <Btn size="sm" icon={<Icon.plus size={13}/>} onClick={() => setPicking(true)}>{t("questions.groupEditorPage.addQuestion", "Add question")}</Btn>
              </div>
            ) : null}
          </div>
        </div>

        {/* Right panel — tree / preview tabs */}
        <div style={{ overflow: mobile ? "visible" : "hidden", background: T.canvas, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div style={{ display: "flex", gap: 4, padding: "14px 16px 0" }}>
            {([["tree","Flow tree",<Icon.network size={13}/>],["preview","Question preview",<Icon.eye size={13}/>]] as const).map(([tid, lbl, ic]) => (
              /* ui-raw: tree/preview switch — SegmentedControl's pattern (group 8) */
              <button key={tid} onClick={() => setRightTab(tid as "tree"|"preview")}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "none", background: rightTab === tid ? T.white : "transparent", color: rightTab === tid ? T.p1 : T.n2, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: T.font }}>
                {ic}{lbl}
              </button>
            ))}
          </div>
          <div style={{ flex: mobile ? "0 0 auto" : 1, overflow: mobile ? "visible" : "auto", padding: 16, minHeight: 0 }}>
            {rightTab === "tree"
              ? <Card style={{ padding: 0 }}><QuestionTreeView group={form}/></Card>
              : items.length === 0
                ? <div style={{ fontSize: 13, color: T.soft, textAlign: "center", paddingTop: 40 }}>{t("questions.groupEditorPage.respondentPreviewRequiresAtLeastOneQuestion", "Respondent preview requires at least one question.")}</div>
                : <Card style={{ padding: 22 }}><QuestionPreviewView group={form}/></Card>}
          </div>
        </div>
      </div>
      {picking ? <ApprovedQuestionPicker exclude={items.map((it) => it.qid)} onPick={addQuestion} onClose={() => setPicking(false)}/> : null}
    </Screen>
  );
}
